'use client';

import { Box, Flex, Link, List, ListItem, Typography } from '@coolblue-development/freddy';
import clickReviewsInSubtitleTrigger from '@coolblue-development/ga4-events/triggers/ProductSubTitle/clickReviewsInSubtitleTrigger';
import selectAllAccessoriesTrigger from '@coolblue-development/ga4-events/triggers/ProductSubTitle/selectAllAccessoriesTrigger';
import selectSubcategoryTrigger from '@coolblue-development/ga4-events/triggers/ProductSubTitle/selectSubcategoryTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { ReviewRating } from '@coolblue-development/next-reviews/client';
import { Product } from '@features/product/typings';
import { ProductContentType, ProductNavigationNode } from '@features/product-page/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { scrollToTarget } from '@utils/utils';

type Props = {
  product: Product;
  accessoryCategoriesUrl: string | undefined;
  reviewsSectionId: string;
  relatedProducts: ProductNavigationNode[] | undefined;
  writeReviewLink?: string;
};

const PageTitle = ({
  product,
  accessoryCategoriesUrl,
  reviewsSectionId,
  relatedProducts,
  writeReviewLink
}: Props) => {
  const { t } = useTranslation('webshop');
  const { trackEvent } = useAnalytics();

  const brandLink = relatedProducts?.find(
    (item: ProductNavigationNode) => item.contentType === ProductContentType.BrandProductTypePage
  );

  const { isMobile } = useBreakpoints();

  const onClickHandler = () => {
    const content: string = brandLink?.title || '';
    trackEvent(
      selectSubcategoryTrigger({
        content
      })
    );
  };

  return (
    <Box>
      <Typography variant='h1'>{product.name}</Typography>
      <List
        orientation='horizontal'
        withDivider
        marker='none'
        sx={theme => ({
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: theme.spacing[200]
        })}
      >
        <ListItem>
          <Flex inline sx={{ verticalAlign: 'bottom' }}>
            <ReviewRating
              onClick={event => {
                if (product.reviewMetaData?.count) scrollToTarget(reviewsSectionId)(event);
                trackEvent(clickReviewsInSubtitleTrigger({ item_id: product.id }));
              }}
              href={`#${reviewsSectionId}`}
              writeReviewLink={writeReviewLink || '#'}
              value={product.reviewMetaData?.averageRating || 0}
              reviewCount={product.reviewMetaData?.count || 0}
              size='medium'
              scoreDisplay={product.reviewMetaData?.count === 0 ? 'none' : 'score-with-max'}
              isOwnedByCustomer={false} // TODO: to be passed from super graph
            />
          </Flex>
        </ListItem>
        {!isMobile && (
          <>
            {accessoryCategoriesUrl && (
              <ListItem>
                <Link
                  href={accessoryCategoriesUrl}
                  rel='nofollow'
                  onClick={() => {
                    trackEvent(
                      selectAllAccessoriesTrigger({ content: t('productPage.allAccessories') })
                    );
                  }}
                >
                  {t('productPage.allAccessories')}
                </Link>
              </ListItem>
            )}
            {brandLink && (
              <ListItem>
                <Link href={brandLink.url} onClick={onClickHandler}>
                  {brandLink.title}
                </Link>
              </ListItem>
            )}
          </>
        )}
      </List>
    </Box>
  );
};

export default PageTitle;
